import { useEffect, useState } from "react";
import { ProtectedRoute } from "@/components/blog/ProtectedRoute";
import { AdminLayout } from "@/components/blog/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { FileText, MessageSquare, Mail, Calendar, Image, Brain } from "lucide-react";
import { Link } from "react-router-dom";

export default function AdminDashboard() {
  const [stats, setStats] = useState({
    totalPosts: 0,
    publishedPosts: 0,
    draftPosts: 0,
    totalInquiries: 0,
  });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    const [postsResult, publishedResult, draftResult, inquiriesResult] = await Promise.all([
      supabase.from("blog_posts").select("id", { count: "exact", head: true }),
      supabase.from("blog_posts").select("id", { count: "exact", head: true }).eq("status", "published"),
      supabase.from("blog_posts").select("id", { count: "exact", head: true }).eq("status", "draft"),
      supabase.from("blog_contact_inquiries").select("id", { count: "exact", head: true }),
    ]);

    setStats({
      totalPosts: postsResult.count || 0,
      publishedPosts: publishedResult.count || 0,
      draftPosts: draftResult.count || 0,
      totalInquiries: inquiriesResult.count || 0,
    });
  };

  const statCards = [
    { title: "Total Posts", value: stats.totalPosts, icon: FileText, color: "text-blue-500" },
    { title: "Published", value: stats.publishedPosts, icon: MessageSquare, color: "text-green-500" },
    { title: "Drafts", value: stats.draftPosts, icon: Calendar, color: "text-orange-500" },
    { title: "Inquiries", value: stats.totalInquiries, icon: Mail, color: "text-purple-500" },
  ];

  const quickLinks = [
    { title: "Manage Posts", href: "/admin/posts", icon: FileText, description: "Create and edit blog posts" },
    { title: "Inquiries", href: "/admin/inquiries", icon: MessageSquare, description: "View contact inquiries" },
    { title: "Follow-ups", href: "/admin/follow-ups", icon: Calendar, description: "Manage email follow-ups" },
    { title: "Email Templates", href: "/admin/email-templates", icon: Mail, description: "Edit email templates" },
    { title: "Media Library", href: "/admin/media", icon: Image, description: "Manage uploaded images" },
    { title: "AI Knowledge Base", href: "/admin/ai-knowledge", icon: Brain, description: "Train Denise AI assistant" },
  ];

  return (
    <ProtectedRoute>
      <AdminLayout>
        <div className="space-y-8">
          <h1 className="text-3xl font-bold">Dashboard</h1>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {statCards.map((stat) => {
              const Icon = stat.icon;
              return (
                <Card key={stat.title}>
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium">{stat.title}</CardTitle>
                    <Icon className={`h-4 w-4 ${stat.color}`} />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{stat.value}</div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div>
            <h2 className="text-2xl font-semibold mb-4">Quick Links</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {quickLinks.map((link) => {
                const Icon = link.icon;
                return (
                  <Link key={link.href} to={link.href}>
                    <Card className="hover:border-primary transition-colors cursor-pointer h-full">
                      <CardHeader>
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-lg bg-primary/10">
                            <Icon className="h-5 w-5 text-primary" />
                          </div>
                          <CardTitle className="text-base">{link.title}</CardTitle>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground">{link.description}</p>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </AdminLayout>
    </ProtectedRoute>
  );
}
